import React from "react";
import BrandBar from "./BrandBar";
import TopNav from "./TopNav";
import ModeControls from "./ModeControls";
import TimeControls from "./TimeControls";
import ShareButton from "./ShareButton";
import AudioControls from "./AudioControls";
import BellIndicator from "./BellIndicator";
import ControlsHint from "./ControlsHint";
import LandmarksLegend from "./LandmarksLegend";
import HotspotPanel from "./HotspotPanel";
import AvatarUploader from "./AvatarUploader";
import UserChip from "./UserChip";
import VRButton from "./VRButton";
import NpcDensitySlider from "./NpcDensitySlider";
import TourButton from "./TourButton";
import ArenaHUD from "./ArenaHUD";
import BroadcastOverlay from "./BroadcastOverlay";
import { useIsNarrow } from "../hooks/useDevice";
import { useAuth } from "../../../auth/AuthContext";

/**
 * ZoneHUD — overlay UI for the Campus Quad.
 *
 *  Right-stack rows (top → bottom, flex column, never overlap):
 *    Row 1 [Orbit/Walk + Sign-in chip]
 *    Row 2  Time controls
 *    Row 2b NPC density slider (hidden on narrow)
 *    Row 3  Share view (hidden on narrow)
 *    Row 4  Avatar pill          ← gets larger marginBottom on the next row
 *    Row 5  VR button (desktop)
 *    Row 6  Bell tolls indicator (last row; extra top gap so it can never
 *           crowd Avatar; only renders when bellVisible=true)
 *
 *  Bottom-right anchor stack:
 *    • TourButton          (just above LandmarksLegend, primary-gold style)
 *    • LandmarksLegend     (bottom: 22)
 *
 *  Bottom-center : floating TopNav glass pill.
 *  Bottom-left   : ControlsHint.
 */
export default function ZoneHUD({
  mode,
  enterOrbit,
  enterWalk,
  timeMode,
  autoLabel,
  setTimeMode,
  bellVisible,
  hotspot,
  onHotspotEnter,
  onHotspotClose,
  activeHotspot,
  getPose,
  muted,
  setMuted,
  volume,
  setVolume,
  avatarApi,
  avatarUnlock,
  xrStore,
  isTouch,
  crowdDensity,
  setCrowdDensity,
  tourActive,
  tourIndex,
  tourCount,
  startTour,
  stopTour,
  arenaApi,
}) {
  const isNarrow = useIsNarrow(900);
  const { user } = useAuth();

  // Compact behaviour on narrow screens.
  const showHint = !isNarrow && !isTouch;
  const showLegend = !isNarrow;

  // Top-right stack container — one flex column.
  const stackStyle = {
    position: "absolute",
    top: isNarrow ? 14 : 22,
    right: isNarrow ? 12 : 24,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: isNarrow ? 8 : 10,
    pointerEvents: "none", // children re-enable
    zIndex: 6,
    // Keep the stack narrower than the viewport on small screens.
    maxWidth: "calc(100vw - 24px)",
  };

  // Row 1 (Orbit/Walk + UserChip). Wraps if needed.
  const rowTopStyle = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: isNarrow ? 6 : 8,
    pointerEvents: "auto",
  };

  return (
    <>
      <BrandBar compact={isNarrow} />

      {/* TOP-RIGHT control stack — single source of layout truth */}
      <div data-testid="zone-hud-right-stack" style={stackStyle}>
        {/* Row 1: Mode (Orbit/Walk) + UserChip */}
        <div data-testid="zone-hud-right-row-1" style={rowTopStyle}>
          <ModeControls
            mode={mode}
            enterOrbit={enterOrbit}
            enterWalk={enterWalk}
            inline
            compact={isNarrow}
          />
          <UserChip compact={isNarrow} />
        </div>

        {/* Row 2: Time controls */}
        <TimeControls
          timeMode={timeMode}
          autoLabel={autoLabel}
          setTimeMode={setTimeMode}
          inline
          compact={isNarrow}
        />

        {/* Row 2b: People-density slider (hidden on very narrow screens) */}
        {!isNarrow && (
          <NpcDensitySlider
            density={crowdDensity}
            setDensity={setCrowdDensity}
            inline
          />
        )}

        {/* Row 3: Share view (hidden on narrow) */}
        {!isNarrow && (
          <ShareButton getPose={getPose} timeMode={timeMode} inline />
        )}

        {/* Row 4: Avatar pill */}
        {avatarApi && avatarUnlock && (
          <div data-testid="zone-avatar-wrapper" style={{ pointerEvents: "auto" }}>
            <AvatarUploader
              avatar={avatarApi.avatar}
              status={avatarApi.status}
              uploadProgress={avatarApi.uploadProgress}
              error={avatarApi.error}
              upload={avatarApi.upload}
              clear={avatarApi.clear}
              isRenderableInScene={avatarApi.isRenderableInScene}
              user={user}
              unlocked={avatarUnlock.unlocked}
              signedIn={avatarUnlock.signedIn}
              onSignIn={avatarUnlock.signIn}
              onBuy={avatarUnlock.buy}
              onVerify={avatarUnlock.verifyNow}
              verifying={avatarUnlock.verifying}
              verifyMessage={avatarUnlock.verifyMessage}
              verifyError={avatarUnlock.verifyError}
              compact={isNarrow}
            />
          </div>
        )}

        {/* Row 5: VR (only on desktop + when WebXR is available) */}
        {xrStore && !isNarrow && (
          <div data-testid="zone-vr-wrapper" style={{ pointerEvents: "auto" }}>
            <VRButton xrStore={xrStore} />
          </div>
        )}

        {/* Row 6: Bell indicator — last row, extra top spacing so the
            Avatar pill above ALWAYS has breathing room (req: "Avatar $9.99
            never touches the Bell Tolls indicator"). */}
        {bellVisible && (
          <div
            data-testid="zone-bell-indicator-wrapper"
            style={{ marginTop: isNarrow ? 12 : 18, pointerEvents: "auto" }}
          >
            <BellIndicator visible inline compact={isNarrow} />
          </div>
        )}
      </div>

      {/* BOTTOM-CENTER nav — floating glass pill */}
      <TopNav dock="bottom" />

      {/* BOTTOM-RIGHT primary action — Tour button anchored above legend.
          On narrow viewports the bottom-docked TopNav lives at bottom:78
          and is ~48px tall, so we lift the Tour button to bottom:138 to
          clear it cleanly (12px breathing room above the nav). */}
      {startTour && stopTour && tourCount > 0 && (
        <TourButton
          active={tourActive}
          count={tourCount}
          currentIndex={tourIndex || 0}
          onStart={startTour}
          onStop={stopTour}
          compact={isNarrow}
          bottomOffset={isNarrow ? 138 : 158}
        />
      )}

      {showHint && <ControlsHint mode={mode} />}
      {showLegend && <LandmarksLegend activeHotspot={activeHotspot} />}
      {arenaApi && (
        <ArenaHUD arenaApi={arenaApi} compact={isNarrow} isTouch={isTouch} />
      )}
      {arenaApi && <BroadcastOverlay arenaApi={arenaApi} />}
      <AudioControls
        muted={muted}
        setMuted={setMuted}
        volume={volume}
        setVolume={setVolume}
        compact={isNarrow}
      />
      <HotspotPanel
        hotspot={hotspot}
        onEnter={onHotspotEnter}
        onClose={onHotspotClose}
      />
    </>
  );
}
